import type { FormInstance } from 'antd'
import { Form } from 'antd'
import {
  capturePlanningSource,
  getPlanningSources,
} from 'features/libra/inspectionPlanning/capturePlanningSource'
import { readPlanningBasis } from 'features/libra/inspectionPlanning/planningBasis'
import { planningPreparationSchema } from 'features/libra/inspectionPlanning/planningPreparationConfig'
import { planningSourceCaptureSchema } from 'features/libra/inspectionPlanning/planningTypes'
import { usePlanningSources } from 'features/libra/inspectionPlanning/usePlanningSources'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { GeckoUnit } from 'types'

const EMPTY_UNIT_IDS: string[] = []

export interface TicketCreationFormValues {
  workflowId: string
  unitIds: string[]
  sourceIds?: Record<string, string>
}

export function useTicketCreationFields({
  form,
  isOpen,
  organizationId,
  groupId,
  selectedWorkflow,
  unitMap,
}: {
  form: FormInstance<TicketCreationFormValues>
  isOpen: boolean
  organizationId: string
  groupId: string
  selectedWorkflow?: LibraWorkflow
  unitMap: ReadonlyMap<string, GeckoUnit>
}) {
  const selectedUnitIds = Form.useWatch('unitIds', form) ?? EMPTY_UNIT_IDS
  const selectedSourceIds = Form.useWatch('sourceIds', form) ?? {}
  const planningSourceCapture =
    selectedWorkflow?.globalContext.planningSourceCapture
  const hasPlanningSourceCapture = planningSourceCapture !== undefined
  const parsedPlanningSourceCapture = planningSourceCaptureSchema.safeParse(
    planningSourceCapture
  )
  const preparationStep = selectedWorkflow?.steps.find(
    (step) => step.component === 'NiiPlanningPreparation'
  )
  const preparationConfigIsValid =
    preparationStep &&
    planningPreparationSchema.safeParse(preparationStep.config).success
  const configurationError = hasPlanningSourceCapture
    ? !parsedPlanningSourceCapture.success
      ? 'The workflow assessment capture settings are invalid. Correct the workflow settings before creating tickets.'
      : !preparationStep || !preparationConfigIsValid
        ? 'The workflow needs a valid NII planning preparation step. Correct its workflow settings before creating tickets.'
        : undefined
    : undefined
  const canCapturePlanningSource =
    hasPlanningSourceCapture &&
    parsedPlanningSourceCapture.success &&
    !configurationError
  const {
    workflows: sourceWorkflows,
    entries: sourceEntries,
    loading: areSourcesLoading,
    workflowError: sourceWorkflowError,
    ticketError: sourceTicketError,
  } = usePlanningSources(
    isOpen && canCapturePlanningSource ? organizationId : undefined,
    groupId,
    canCapturePlanningSource ? selectedUnitIds : []
  )
  const sourcesByUnit = useMemo(
    () =>
      new Map(
        selectedUnitIds.map((unitId) => [
          unitId,
          getPlanningSources(sourceEntries, sourceWorkflows, {
            organization: organizationId,
            unit: unitId,
            'libra-ticket-group': groupId,
          }),
        ])
      ),
    [groupId, organizationId, selectedUnitIds, sourceEntries, sourceWorkflows]
  )
  const [sourceError, setSourceError] = useState<string>()

  useEffect(() => {
    if (
      !canCapturePlanningSource ||
      areSourcesLoading ||
      sourceWorkflowError ||
      sourceTicketError
    )
      return

    const sourceIds: Record<string, string> = {
      ...(form.getFieldValue('sourceIds') ?? {}),
    }
    let changed = false
    selectedUnitIds.forEach((unitId) => {
      const sources = sourcesByUnit.get(unitId) ?? []
      if (
        sourceIds[unitId] &&
        !sources.some((source) => source.id === sourceIds[unitId])
      ) {
        delete sourceIds[unitId]
        changed = true
      }
      if (!sourceIds[unitId] && sources.length === 1) {
        sourceIds[unitId] = sources[0].id
        changed = true
      }
    })
    if (changed) form.setFieldValue('sourceIds', sourceIds)
  }, [
    areSourcesLoading,
    canCapturePlanningSource,
    form,
    selectedUnitIds,
    sourceTicketError,
    sourceWorkflowError,
    sourcesByUnit,
  ])

  const reset = useCallback(() => {
    form.setFieldValue('sourceIds', undefined)
    setSourceError(undefined)
  }, [form])
  const onSourceChange = useCallback(() => {
    setSourceError(undefined)
  }, [])

  const sourceSelectionReady = selectedUnitIds.every((unitId) => {
    const selectedId = selectedSourceIds[unitId]
    return (sourcesByUnit.get(unitId) ?? []).some(
      (source) => source.id === selectedId
    )
  })
  const isReady =
    !configurationError &&
    !(
      canCapturePlanningSource &&
      selectedUnitIds.length > 0 &&
      (areSourcesLoading ||
        sourceWorkflowError ||
        sourceTicketError ||
        !sourceSelectionReady)
    )

  const prepareInitialFields = (
    values: TicketCreationFormValues,
    workflow: LibraWorkflow
  ): Map<string, FieldValues> | undefined => {
    const initialFieldsByUnit = new Map<string, FieldValues>()
    const rawCaptureConfig = workflow.globalContext.planningSourceCapture
    if (rawCaptureConfig === undefined) return initialFieldsByUnit

    const captureConfig =
      planningSourceCaptureSchema.safeParse(rawCaptureConfig)
    const preparation = workflow.steps.find(
      (step) => step.component === 'NiiPlanningPreparation'
    )
    if (!captureConfig.success) {
      setSourceError(
        'The workflow assessment capture settings are invalid. Correct the workflow settings before creating tickets.'
      )
      return undefined
    }
    if (
      !preparation ||
      !planningPreparationSchema.safeParse(preparation.config).success
    ) {
      setSourceError(
        'The workflow needs a valid NII planning preparation step. Correct its workflow settings before creating tickets.'
      )
      return undefined
    }
    if (areSourcesLoading || sourceWorkflowError || sourceTicketError) {
      setSourceError(
        'Completed NII assessments could not be loaded. Wait for the source list to load, then try again.'
      )
      return undefined
    }

    try {
      values.unitIds.forEach((unitId) => {
        const sources = getPlanningSources(sourceEntries, sourceWorkflows, {
          organization: organizationId,
          unit: unitId,
          'libra-ticket-group': groupId,
        })
        const source = sources.find(
          (ticket) => ticket.id === values.sourceIds?.[unitId]
        )
        if (!source)
          throw new Error(
            'Choose a current completed NII assessment for every selected asset.'
          )

        const sourceWorkflow = sourceWorkflows.find(
          (candidate) => candidate.id === source.workflowId
        )
        if (!sourceWorkflow)
          throw new Error(
            'The selected NII assessment workflow is no longer available. Choose another assessment.'
          )

        let capturedFields: FieldValues
        try {
          capturedFields = capturePlanningSource(
            source,
            sourceWorkflow,
            captureConfig.data,
            new Date().toISOString()
          )
        } catch {
          throw new Error(
            'The selected NII assessment could not be captured. Choose another assessment or correct its workflow settings.'
          )
        }
        const basis = readPlanningBasis(
          preparation.config,
          capturedFields,
          workflow.globalContext
        )
        if (basis.status !== 'ready')
          throw new Error(
            'The selected assessment cannot be read by this planning workflow. Choose another assessment or correct the workflow preparation and strategy settings.'
          )
        initialFieldsByUnit.set(unitId, capturedFields)
      })
    } catch (error) {
      setSourceError(
        error instanceof Error
          ? error.message
          : 'Could not validate the selected NII assessments.'
      )
      return undefined
    }

    return initialFieldsByUnit
  }

  return {
    isReady,
    reset,
    prepareInitialFields,
    sectionProps: {
      configurationError,
      sourceError,
      canCapturePlanningSource,
      areSourcesLoading,
      hasSourceLoadError: Boolean(sourceWorkflowError || sourceTicketError),
      selectedUnitIds,
      sourcesByUnit,
      unitMap,
      onSourceChange,
    },
  }
}
